import type { ReactNode } from 'react'
import cx from 'classnames'
import styles from './CollaboratorItem.module.css'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { truncate } from 'lodash-es'
import { Tooltip } from '@/components/Tooltip'
import type { Id } from '@/infra/id'
import { Link } from '@/components/Link'
import { links } from '@/utils/link'

export interface CollaboratorItemProps {
  displayName: string
  email: string
  phone: string
  team?: { name: string }
  type: string
  formattedType: string
  formattedRole: string
  userPublicId: Id
  isOnline: boolean
  avatar: AvatarSubject
  canViewUserDetails: boolean
  canViewCollaboratorInfo: boolean
  canViewCollaboratorEmail: boolean
  canViewCollaboratorPhoneNumber: boolean
}

export function CollaboratorItem({
  displayName,
  email,
  phone,
  team,
  type,
  formattedType,
  formattedRole,
  userPublicId,
  isOnline,
  avatar,
  canViewUserDetails,
  canViewCollaboratorInfo,
  canViewCollaboratorEmail,
  canViewCollaboratorPhoneNumber,
}: CollaboratorItemProps) {
  return (
    <div
      className={cx(
        styles.root,
        team && styles.withTeam,
        email && styles.withEmail,
        phone && styles.withPhone,
      )}
      data-test={`collaborator-item-${displayName}`}
    >
      <div className={styles.info}>
        <LinkToUserDetails
          canViewUserDetails={canViewUserDetails}
          collaboratorId={userPublicId}
        >
          <p
            data-test={`collaborator-item-name-${displayName}`}
            className={styles.title}
          >
            {displayName}
          </p>
        </LinkToUserDetails>

        <div className={styles.subtitle}>
          {canViewCollaboratorInfo && (
            <div className={styles.subtitleInfo}>
              {team && <p className={styles.team}>{team.name}</p>}
              {canViewCollaboratorEmail && (
                <>
                  {email.length >= 38 ? (
                    <Tooltip content={email}>
                      <p className={styles.email}>
                        {truncate(email, { length: 38 })}
                      </p>
                    </Tooltip>
                  ) : (
                    <p className={styles.email}>{email}</p>
                  )}
                </>
              )}
              {canViewCollaboratorPhoneNumber && (
                <p className={styles.phone}>{phone}</p>
              )}
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <p>
            <span
              className={cx(
                type === 'Client' && styles.client,
                type === 'Pro' && styles.pro,
              )}
            >
              {formattedType}
            </span>
            <span className={styles.separator}>|</span>
            <span data-test={`collaborator-item-role-${displayName}`}>
              {formattedRole}
            </span>
          </p>

          <LinkToUserDetails
            canViewUserDetails={canViewUserDetails}
            collaboratorId={userPublicId}
          >
            <div className={styles.avatarContainer}>
              <Avatar
                badge={isOnline ? 'online' : 'offline'}
                size={32}
                subject={avatar}
              />
            </div>
          </LinkToUserDetails>
        </div>
      </div>
    </div>
  )
}

interface LinkToUserDetailsProps {
  children: ReactNode
  canViewUserDetails: boolean
  collaboratorId: Id
}

/**
 * Internal component for linking to user details.
 */
function LinkToUserDetails({
  children,
  canViewUserDetails,
  collaboratorId,
}: LinkToUserDetailsProps) {
  return canViewUserDetails ? (
    <Link
      className={styles.hqUserDetailsLink}
      to={links.userDetails(collaboratorId)}
    >
      {children}
    </Link>
  ) : (
    children
  )
}
